import React, {
	useMemo,
	useState,
} from 'react';
import {
	useQuery,
	useQueryClient,
} from 'react-query';
import {
	Col,
	ConfigProvider,
	Flex,
	Row,
} from 'antd';
import {
	EstimateStatus,
	EstimatorModeTypes,
	FixtureCounterpartyTypes,
} from '@shared/utils/constants';
import type { PeriodSchedule } from '@api/features/fixtures/getFixtureDetails';
import { getCounterparties } from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import PeriodScheduleTable from '@client/screens/fixtures/tc/PeriodScheduleTable';
import Table from '@client/components/Table/Table';
import BrokersTable from '@client/screens/fixtures/tc/BrokersTable';
import Card from '@client/components/Card/Card';
import Modal from '@client/components/Modal/Modal';
import { useCentralStore } from './context/CentralStoreContext';
import tcEstimateItems from './helpers/tcEstimateItems';
import EstimateConfigurationCard from './components/EstimateConfigurationCard';
import RobsAndPrices from './components/RobsAndPrices';
import PortRotationCard from './PortRotationCard';
import styles from './components/styles/EstimateConfigurationCard.module.css';
import { useEstimate } from './context/EstimatorContext';
import { useEstimateChangesProvider } from './context/EstimateChangesContext';
import TcTermsModalContent from './TcTermsModalContent';

const TcEstimateDetails = ({ mode }: { mode: EstimatorModeTypes }) => {
	const [showBrokersModal, setShowBrokersModal] = useState(false);
	const [showTcTermsModal, setShowTcTermsModal] = useState(false);

	const queryClient = useQueryClient();

	const {
		refreshFixture,
		onUpdateFixture,
		fixtureDetails,
		refreshEverything,
		setPendingPeriodScheduleChanges,
		selectedEstimate,
		tcTerms,
		onUpdateTcTerms,
	} = useCentralStore();

	const {
		onUpdateEstimate,
		estimate,
	} = useEstimate();

	const {
		onSaveChanges,
	} = useEstimateChangesProvider();

	const { userInfo } = useAuth();
	const { componentDisabled } = ConfigProvider.useConfig();

	const {
		data: charterers,
	} = useQuery(
		'charterers',
		() => getCounterparties(FixtureCounterpartyTypes.CHARTEREROWNER),
	);

	const onCloseModal = () => {
		setShowBrokersModal(false);
	};

	const onUpdateTotalBrokerCommission = async (comm: number) => {
		await onUpdateEstimate({
			totalTcBrokerCommission: comm,
		});
		queryClient.invalidateQueries(['brokers']);
	};

	const isFixed = estimate?.status === EstimateStatus.Fixed;

	const items = useMemo(() => (
		(fixtureDetails == null || tcTerms == null) ? [] :
			tcEstimateItems({
				onUpdateTcTerms,
				tcTerms,
				fixtureDetails,
				estimate,
				charterers,
				fixingCompanies: userInfo.fixingCompanies,
				setShowBrokersModal,
				setShowTcTermsModal,
				onUpdateTotalBrokerCommission,
				componentDisabled,
			})
	), [
		fixtureDetails,
		tcTerms,
		estimate,
		charterers,
		userInfo.fixingCompanies,
		onUpdateTcTerms,
		componentDisabled,
	]);

	if (fixtureDetails == null || selectedEstimate == null || tcTerms == null) {
		// just to make TS happy. These checks are done in the parent component
		return (<Card>404</Card>);
	}

	const onBrokerRefresh = async () => {
		await onSaveChanges();
		await refreshEverything();
	};

	const updatePeriodSchedule = async (
		updatedEntry: PeriodSchedule,
	) => {
		const entry = {
			id: updatedEntry.id,
			hireRate: updatedEntry.hireRate,
		};

		await onUpdateFixture({
			periodSchedule: fixtureDetails.periodSchedule.map((scheduleEntry) => (
				scheduleEntry.id === updatedEntry.id ? updatedEntry : scheduleEntry
			)),
		});
		// @ts-ignore (this works perfectly and is safe, TS is tripping)
		setPendingPeriodScheduleChanges((prev: PeriodSchedule[]) => {
			const exists = prev.some((e) => e.id === updatedEntry.id);

			return exists ?
				prev.map((e) => (e.id === updatedEntry.id ? entry : e)) :
				[...prev, entry];
		});
	};

	const locked = isFixed || (fixtureDetails.fixed != null && fixtureDetails.fixed);

	return (
		<>
			<Row gutter={[8, 8]}>
				<Col span={24}>
					<EstimateConfigurationCard />
				</Col>
				<Col span={24}>
					<RobsAndPrices />
				</Col>
				<Col span={24}>
					<Card slim>
						<Flex vertical gap={16}>
							<Table
								pagination={false}
								size="small"
								bordered
								dataSource={[{}]}
								columns={items}
							/>
							<PeriodScheduleTable
								data-testid="est-period-schedule"
								fixture={fixtureDetails}
								periodSchedule={fixtureDetails.periodSchedule}
								onRowChange={isFixed ? undefined : updatePeriodSchedule}
								fixtureCurrency={selectedEstimate.currency}
								refreshFixture={refreshFixture}
								hireEditable={!isFixed}
								modalEditable={!isFixed}
								tcTerms={fixtureDetails.tcTerms}
								afterSave={async () => {
									await onSaveChanges();
								}}
							/>
						</Flex>
					</Card>
				</Col>
				<Col span={24}>
					<PortRotationCard tcEstimate mode={mode} />
				</Col>
			</Row>
			<Modal
				open={showBrokersModal}
				footer={null}
				className={styles.fuelEuPenaltyModal}
				onCancel={onCloseModal}
				afterClose={onCloseModal}
				okButtonProps={{
					onClick: onCloseModal,
				}}
				width="75%"
			>
				<BrokersTable
					tcTerms={fixtureDetails.tcTerms}
					refreshFixture={onBrokerRefresh}
					fixtureDetails={fixtureDetails}
				/>
			</Modal>
			<Modal
				width="80%"
				open={showTcTermsModal}
				closeIcon={false}
				onCancel={() => setShowTcTermsModal(false)}
				footer={null}
			>
				<TcTermsModalContent
					fixtureDetails={fixtureDetails}
					tcTerms={fixtureDetails.tcTerms}
					onClose={() => setShowTcTermsModal(false)}
					onSaveChanges={onSaveChanges}
					locked={locked}
					refreshDetails={refreshEverything}
					bunkers={fixtureDetails.bunkers}
					fixtureCurrency={selectedEstimate.currency}
					availableFuelGrades={estimate?.vesselFuelGrades}
					open={showTcTermsModal}
				/>
			</Modal>
		</>
	);
};

export default TcEstimateDetails;
